/**
 * Versioned difficulty model.
 *
 * The estimated difficulty is a linear blend of normalised puzzle features. It is a
 * *model*, not ground truth: every input feature is exported through the developer lab and
 * the CLI validator so the weights can be recalibrated against real play data later,
 * by adding a new version rather than editing this one.
 *
 * The scales are set so the shipped campaign spreads across the range rather than bunching
 * at the top: board weight saturates near a 1,300-cell board and depth near forty-five
 * waves, which are roughly the largest values the expert band produces.
 */
export interface DifficultyModel {
  readonly version: number;
  readonly weights: {
    /** Weight on how much of the board is covered by arrow paths. */
    readonly boardWeight: number;
    readonly dependencyDepth: number;
    readonly forcedStepRatio: number;
    /** Weight on how *closed* the opening position is, as a share of the arrow count. */
    readonly closedOpening: number;
    /** Weight on how few moves are available on average during the solve. */
    readonly closedAverage: number;
    readonly occupancy: number;
    readonly edgeDensity: number;
  };
  /** Feature normalisation ceilings; values above these saturate at 1. */
  readonly scales: {
    readonly occupiedCells: number;
    readonly dependencyDepth: number;
    /** Share of arrows legal at the start that counts as "wide open". */
    readonly openRatio: number;
    /** Share of arrows legal on average that counts as "wide open". */
    readonly meanRatio: number;
    readonly edgesPerArrow: number;
  };
}

export const DIFFICULTY_MODEL_V1: DifficultyModel = {
  version: 1,
  weights: {
    boardWeight: 0.22,
    dependencyDepth: 0.24,
    forcedStepRatio: 0.14,
    closedOpening: 0.16,
    closedAverage: 0.12,
    occupancy: 0.06,
    edgeDensity: 0.06,
  },
  scales: {
    occupiedCells: 1300,
    dependencyDepth: 45,
    openRatio: 0.6,
    meanRatio: 0.4,
    edgesPerArrow: 6,
  },
};

export const CURRENT_DIFFICULTY_MODEL = DIFFICULTY_MODEL_V1;

export function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}
